import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { LinkDefinition } from '../interfaces/link';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { capitalize } from '../lib/capitalize';
import { getAllRouteData, getAllRouteDatas } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { isNotFoundRoute } from '../lib/is-not-found-route';
import { doIndexMetaTag, getMetaImageTags, getMetaTitleTags } from '../lib/meta-helpers';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { LinkService } from './link.service';
import { StateService } from './state.service';

export const defaultFavicons: LinkDefinition[] = [
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '48x48',
    href: '/assets/favicon/favicon.png',
  },
  {
    rel: 'icon',
    type: 'image/svg+xml',
    href: '/assets/favicon/favicon.svg',
  },
  {
    rel: 'shortcut icon',
    href: '/assets/favicon/favicon.ico',
  },
  {
    rel: 'apple-touch-icon',
    href: '/assets/favicon/apple-touch-icon.png',
  },
  {
    rel: 'mask-icon',
    color: '#4353FF',
    href: '/assets/favicon/safari-pinned-tab.svg',
  },
];
@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private meta = inject(Meta);
  private link = inject(LinkService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private state = inject(StateService);
  private title = inject(Title);
  private browserService = inject(BrowserService);
  private config = inject(AppConfigService);
  private document = inject<Document>(DOCUMENT);
  lastUrl: string;

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.handleMeta(event.urlAfterRedirects);
    });
  }

  private getFavicons() {
    return defaultFavicons.map(icon => {
      return {
        ...icon,
        href: this.state.unseenActivity ? icon.href.replace('favicon.', 'favicon-activity.') : icon.href,
      };
    });
  }

  private getManifest() {
    if (this.browserService.isApp()) return '/app-manifest.json';
    if (!this.config.isTenantDomain) return;
    const data = getAllRouteData(this.route.snapshot);
    const tenant = data.tenant || data.gallery?.tenant;
    if (!tenant) return;
    if (!data.gallery) return `/manifest/${tenant.path}`;
    return addGalleryPassword(`/manifest/${tenant.id}/${data.gallery.id}`, data.gallery.password);
  }

  public setFavicon(url: string) {
    defaultFavicons.forEach(link => {
      this.link.updateLink({
        ...link,
        href: url,
      });
    });
  }

  public setTitle(title: string) {
    const path = getFullPath(this.route.snapshot);
    if (path[0] === 'a') {
      if (path[1] === 'gallery') {
        const galleryId = path[2];
        const count = this.state.unseenActivityCountMap.get(galleryId);
        if (count) {
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      } else if (this.state.unseenActivity > 0) {
        const isShared = path[1] === 'home' && this.state.activeTenantRole === 'shared';

        if (!isShared) {
          const count = this.state.unseenActivity;
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      }
    }

    this.title.setTitle(title);
  }

  public async handleMeta(url: string = this.lastUrl) {
    if (!url && !this.lastUrl) return;
    this.lastUrl = url;
    const metaTags = this.getMetaTags();
    const title = this.getTitle();
    this.setTitle(title);

    const allMetaTags = this.meta.getTags('name').concat(this.meta.getTags('property'));
    allMetaTags.forEach(tag => {
      const shouldRemove = !metaTags.find(t => {
        if (t.name && tag.name) return t.name === tag.name;
        return false;
      });
      if (shouldRemove) {
        this.meta.removeTag(`name='${tag.name}'`);
      }
    });
    metaTags.forEach(tag => this.meta.updateTag(tag));

    const linkTags = this.getLinkTags();
    const allLinkTags = Array.from(this.document.head.querySelectorAll('link'));
    allLinkTags.forEach(tag => {
      const link = this.link.elementToLink(tag);
      const shouldRemove = ['alternate'].includes(link.rel) && !linkTags.find(l => this.link.isSameLink(l, link));

      if (shouldRemove) {
        this.link.removeLink(
          `link[rel='${link.rel}']${link.hreflang ? `[hreflang='${link.hreflang}']` : ''}${link.type ? `[type='${link.type}']` : ''}`
        );
      }
    });

    linkTags.forEach(link => this.link.updateLink(link));
  }

  private getCanonical() {
    const path = getFullPath(this.route.snapshot, true, false);
    const origin = this.browserService.getOrigin();
    return `${origin}/${path.join('/')}`;
  }

  private getTitleFromRoute(route: ActivatedRouteSnapshot): string {
    const path = getFullPath(route);
    if (isNotFoundRoute(path, { notFound: this.state.nextPageIsNotFound })) {
      return '404 · Page not found';
    }

    return capitalize(path.pop()) + ' · Picflow';
  }

  public getTitle() {
    const datas = getAllRouteDatas(this.route.snapshot);
    const title = datas.reduce((title, data) => {
      if (data.meta?.title) return data.meta.title as string;
      return title;
    }, undefined as string);

    if (title) return title;
    return this.getTitleFromRoute(this.route.snapshot);
  }

  private combineMetas(metas: { title?: string; tags?: MetaDefinition[]; links: LinkDefinition[] }[]): {
    title?: string;
    tags: MetaDefinition[];
    links: LinkDefinition[];
  } {
    const combined: { title: string; tags: MetaDefinition[]; links: LinkDefinition[] } = {
      title: undefined,
      tags: [],
      links: [],
    };

    metas.forEach(meta => {
      if (meta.title) {
        combined.title = meta.title;
      }
      if (meta.tags) {
        meta.tags.forEach(tag => {
          // Avoid duplicates
          const index = combined.tags.findIndex(t => {
            if (t.name && tag.name) return t.name === tag.name;
            if (t.property && tag.property) return t.property === tag.property;
            return false;
          });
          if (index !== -1) {
            // remove the existing tag
            combined.tags.splice(index, 1);
          }

          combined.tags.push(tag);
        });
      }
      if (meta.links) {
        meta.links.forEach(link => {
          // Avoid duplicates
          const ignoreisSame = link.rel === 'preconnect' || link.rel === 'preload';
          const index = ignoreisSame ? -1 : combined.links.findIndex(t => this.link.isSameLink(t, link));
          if (index !== -1) {
            // remove the existing link
            combined.links.splice(index, 1);
          }

          combined.links.push(link);
        });
      }
    });

    return combined;
  }

  private getMetaTags() {
    const datas = getAllRouteDatas(this.route.snapshot);

    const globalTags: MetaDefinition[] = [
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:site_name',
        content: 'Picflow',
      },
      ...getMetaTitleTags(this.getTitleFromRoute(this.route.snapshot)),
      ...getMetaImageTags(`${this.config.correctPicflowUrl}/assets/shared/default.jpg`),
      doIndexMetaTag,
      { property: 'og:url', content: this.getCanonical() },
      { name: 'theme-color', content: '#FFFFFF' },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
      },
      {
        name: 'HandheldFriendly',
        content: 'true',
      },
      // {
      //   name: 'og:locale',
      //   content: defaultLanguage,
      // },
    ];

    return this.combineMetas([
      {
        tags: globalTags,
      },
      ...datas.map(d => d.meta).filter(Boolean),
    ]).tags;
  }

  private getLinkTags() {
    const datas = getAllRouteDatas(this.route.snapshot);

    const fontUrls = [
      '/assets/fonts/subset-e0b619674400d7387d042996f58c9e54.woff2',
      '/assets/fonts/subset-2d9d98a25b1128f89314b16b8937e605.woff2',
      '/assets/fonts/subset-b19712e21f8854ab9e035d1c465914bd.woff2',
    ];

    const globalLinks: LinkDefinition[] = [
      {
        rel: 'canonical',
        href: this.getCanonical(),
      },
      {
        rel: 'manifest',
        href: this.getManifest() || '/manifest.webmanifest',
      },
      ...this.getFavicons(),
      ...fontUrls.map(url => {
        return {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: url,
          crossOrigin: 'anonymous',
        };
      }),
    ];

    return this.combineMetas([
      {
        links: globalLinks,
      },
      ...datas.map(d => d.meta).filter(Boolean),
    ]).links;
  }
}
